import {useState} from 'react'
import {useSnapshot} from 'valtio'
import {store, t} from '@/form.js'
import {formatAmount} from '@/config/amount.js'
import {AppButton} from '@/components/AppButton/AppButton.jsx'
import {Certificates} from '@/components/Certificates/Certificates.jsx'
import {EyeIcon, IconButton, TextField} from '@/components/TextField/TextField.jsx'
import {CardBrandIcon} from '@/components/CardBrandIcon/CardBrandIcon.jsx'
import {getCardBrand, PAYMENT_FIELD_NAMES, validate} from '@/pages/CardFlow/CardPage/validate.js'
import styles from './CardPage.module.css'

const defaultState = {
  [PAYMENT_FIELD_NAMES.cardNumber]: '',
  [PAYMENT_FIELD_NAMES.expDate]: '',
  [PAYMENT_FIELD_NAMES.cvv2]: '',
  [PAYMENT_FIELD_NAMES.nameoncard]: '',

  isCvcEnabled: true,
}

// Each field auto-advances focus once, so that correcting an earlier field
// later on does not yank the caret away from the user.
const nextIsUsed = {
  [PAYMENT_FIELD_NAMES.cardNumber]: false,
  [PAYMENT_FIELD_NAMES.expDate]: false,
  [PAYMENT_FIELD_NAMES.cvv2]: false,
  [PAYMENT_FIELD_NAMES.nameoncard]: false,
}

const cardFields = [
  PAYMENT_FIELD_NAMES.cardNumber,
  PAYMENT_FIELD_NAMES.nameoncard,
  PAYMENT_FIELD_NAMES.expDate,
  PAYMENT_FIELD_NAMES.cvv2,
]

export function CardPage() {
  const appState = useSnapshot(store.appSlice.state)
  const formState = useSnapshot(store.cardFormSlice.state)

  const [showCVC, setShowCVC] = useState(false)
  const [state, setState] = useState(defaultState)
  const [fieldErrors, setFieldErrors] = useState({})

  /**
   * `advance` is only set while the user is typing. On blur we still re-run the
   * validator (so a half-filled field reports its error) but must not move the
   * caret, or clicking straight from the card number into CVV would bounce
   * focus back to the expiry field.
   */
  const applyValue = (name, value, advance) => {
    const isActive = document.activeElement?.name === name

    setState(prev => {
      const {fixedValue, errors, next} = validate[name](value, prev, isActive)

      setFieldErrors(prevErrors => ({
        ...prevErrors,
        [name]: errors.length ? errors[0] : '',
      }))

      if (advance && next && !nextIsUsed[next]) {
        setTimeout(() => {
          const el = document.querySelector(`[name="${next}"]`)
          if (el) {
            el.focus()
            nextIsUsed[next] = true
          }
        })
      }

      return {...prev, [name]: fixedValue}
    })
  }

  const handleInputChange = (e) => applyValue(e.target.name, e.target.value, true)
  const handleInputBlur = (e) => applyValue(e.target.name, e.target.value, false)

  const processing = () => {
    let valid = true
    const fieldErrs = {}

    cardFields.forEach(name => {
      const {errors} = validate[name](state[name], state)
      if (errors.length) {
        valid = false
        fieldErrs[name] = errors[0]
      }
    })

    setFieldErrors({
      ...fieldErrors,
      ...fieldErrs,
    })

    if (!valid) {
      return
    }

    store.cardFormSlice.actions.fetchFormPay({
      pan: state[PAYMENT_FIELD_NAMES.cardNumber].replace(/ /g, ''),
      name: state[PAYMENT_FIELD_NAMES.nameoncard],
      cvc: state[PAYMENT_FIELD_NAMES.cvv2],
      expire_month: state[PAYMENT_FIELD_NAMES.expDate].slice(0, 2),
      expire_year: state[PAYMENT_FIELD_NAMES.expDate].slice(2, 4),
      issuer_name: appState.settings?.paymentMethod?.issuer_name,
    })
  }

  const isPaying = formState.pay.isLoading
  const cardBrand = getCardBrand(state[PAYMENT_FIELD_NAMES.cardNumber])

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault()
        processing()
      }}
    >
      <p className={`caption ${styles.heading}`}>
        {t('CardPage:Title')}
      </p>

      <TextField
        id="CardForm-PanInput"
        name={PAYMENT_FIELD_NAMES.cardNumber}
        placeholder={t('FormField:CardNumber')}
        mask="0000 0000 0000 0000 000"
        value={state[PAYMENT_FIELD_NAMES.cardNumber]}
        disabled={isPaying}
        error={fieldErrors[PAYMENT_FIELD_NAMES.cardNumber]}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        inputProps={{inputMode: 'numeric', autoComplete: 'cc-number'}}
        endAdornment={<CardBrandIcon brand={cardBrand}/>}
      />

      <div className={styles.row}>
        <TextField
          id="CardForm-ValidThruInput"
          name={PAYMENT_FIELD_NAMES.expDate}
          placeholder={t('FormField:CardMonthYear')}
          mask="00/00"
          value={state[PAYMENT_FIELD_NAMES.expDate]}
          disabled={isPaying}
          error={fieldErrors[PAYMENT_FIELD_NAMES.expDate]}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          inputProps={{inputMode: 'numeric', autoComplete: 'cc-exp'}}
        />

        <TextField
          id="CardForm-CvvInput"
          name={PAYMENT_FIELD_NAMES.cvv2}
          placeholder={t('FormField:CardCVC')}
          mask="000"
          value={state[PAYMENT_FIELD_NAMES.cvv2]}
          disabled={isPaying}
          error={fieldErrors[PAYMENT_FIELD_NAMES.cvv2]}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          inputProps={{
            inputMode: 'numeric',
            autoComplete: 'cc-csc',
            type: showCVC ? 'text' : 'password',
          }}
          /* The design shows a bare CVV field, so the reveal toggle only
             appears once there is something to reveal. */
          endAdornment={state[PAYMENT_FIELD_NAMES.cvv2] ? (
            <IconButton
              label={showCVC ? t('FormField:HideCvc') : t('FormField:ShowCvc')}
              onClick={() => setShowCVC(!showCVC)}
            >
              <EyeIcon off={showCVC}/>
            </IconButton>
          ) : null}
        />
      </div>

      <div className={styles.spacer}/>

      <div className={styles.amount}>
        <p className={`caption ${styles.amountLabel}`}>
          {t('CardPage:AmountLabel')}
        </p>
        <p className={styles.amountValue}>
          {formatAmount(formState.formInfo)}
        </p>
      </div>

      <AppButton
        type="submit"
        className={styles.submit}
        loading={isPaying}
      >
        {t('Action:Pay')}
      </AppButton>

      <Certificates className={styles.certificates}/>
    </form>
  )
}
